import type { WebSocketContextValue } from '../../components/webSocketConnection/WebSocket'
import {
    AiChatEvent,
    AiChatEvent_EventType,
    AiChatRequest_Action,
    WebSocketRequestMessage,
    WebSocketResponseMessage,
} from '../../proto/generated/websocket'
import type { AiChatWsMessage } from './aiChat'

export function aiChatEventToWsMessage(event: AiChatEvent): AiChatWsMessage | null {
    if (event.eventType === AiChatEvent_EventType.ACCEPTED) {
        return {
            type: 'accepted',
            conversation_id: event.conversationId,
            status: event.status,
            metadata: event.turnId ? { turn_id: event.turnId } : undefined,
        }
    }
    if (event.eventType === AiChatEvent_EventType.CHAT_EVENT) {
        let metadata: Record<string, unknown> = {}
        if (event.metadataJson) {
            try {
                metadata = JSON.parse(event.metadataJson) as Record<string, unknown>
            } catch {
                // ignore malformed metadata
            }
        }
        return {
            type: 'chat_event',
            conversation_id: event.conversationId,
            task_id: event.taskId,
            status: event.status,
            reply: event.reply,
            metadata,
        }
    }
    return null
}

export function decodeAiChatFromWsFrame(data: Uint8Array): AiChatWsMessage | null {
    const response = WebSocketResponseMessage.decode(data)
    if (!response.aiChatEvent) return null
    return aiChatEventToWsMessage(response.aiChatEvent)
}

export type AiChatRequestMetadata = {
    agent?: 'screener' | 'chart' | 'orchestrator'
    conversation_filters?: { filters: unknown }
    chart_context?: Record<string, unknown>
    surface?: {
        route?: string
        ready_domains?: string[]
        surface_token?: string
    }
    conversation_domain?: 'chart' | 'screener'
    continues_turn?: boolean
    fork_notice?: string
}

export function sendAiChatMessage(
    ws: WebSocketContextValue,
    authToken: string,
    conversationId: string,
    message: string,
    metadata?: AiChatRequestMetadata,
): void {
    const request: WebSocketRequestMessage = {
        authToken,
        aiChatRequest: {
            action: AiChatRequest_Action.SEND_MESSAGE,
            conversationId,
            message,
            authToken,
            taskId: '',
            metadataJson: metadata ? JSON.stringify(metadata) : '',
        },
        subscription: undefined,
        sendInitialData: false,
    }
    ws.sendMessage(WebSocketRequestMessage.encode(request).finish())
}

export function sendAiChatAbort(
    ws: WebSocketContextValue,
    authToken: string,
    conversationId: string,
    taskId: string,
): void {
    const request: WebSocketRequestMessage = {
        authToken,
        aiChatRequest: {
            action: AiChatRequest_Action.ABORT,
            conversationId,
            taskId,
            message: '',
            authToken: '',
            metadataJson: '',
        },
        subscription: undefined,
        sendInitialData: false,
    }
    ws.sendMessage(WebSocketRequestMessage.encode(request).finish())
}

export function isMainWsConnected(ws: WebSocketContextValue | null): boolean {
    return ws?.socket?.readyState === WebSocket.OPEN
}

/** @deprecated Use ws.sendMessage directly — the WebSocket provider queues until open. */
export function waitForMainWsOpen(ws: WebSocketContextValue | null, timeoutMs = 5000): Promise<boolean> {
    if (!ws?.socket) return Promise.resolve(false)
    if (ws.socket.readyState === WebSocket.OPEN) return Promise.resolve(true)
    if (ws.socket.readyState === WebSocket.CLOSED || ws.socket.readyState === WebSocket.CLOSING) {
        return Promise.resolve(false)
    }
    return new Promise((resolve) => {
        const timer = window.setTimeout(() => finish(false), timeoutMs)
        const finish = (ok: boolean) => {
            window.clearTimeout(timer)
            ws.removeOpenListener(onOpen)
            resolve(ok)
        }
        const onOpen = () => finish(true)
        ws.addOpenListener(onOpen)
    })
}
